/**
 * Staff Dashboard - Unified View for Case Management
 * =====================================================
 * 
 * Dedicated dashboard for staff (accountants, data specialists) combining:
 * - Queue management (claim/release)
 * - Case readiness status
 * - Extracted document summaries
 * - Sign-off workflow
 * 
 * P3: Dashboard Integration
 * @author ClaimSync Team
 * @created August 2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import AppLayout from '../components/AppLayout';
import { api } from '../utils/api';
import CaseReadinessPanel from '../components/CaseReadinessPanel';
import ExtractedDocumentsPanel from '../components/ExtractedDocumentsPanel';
import NowLineHeader, { NowLineStats } from '../components/NowLineHeader';
import {
  Users,
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  ChevronRight,
  Briefcase,
  Building2,
  DollarSign,
  Search,
  Filter,
  Eye,
  Edit,
  Send,
  X,
  Check
} from 'lucide-react';

interface QueueCase {
  id: string;
  business_name: string;
  client_name: string;
  law_firm?: string;
  status: string;
  pipeline_stage?: string;
  readiness_score?: number;
  is_ready?: boolean;
  claimed_by_id?: string;
  claimed_by_name?: string;
  claimed_at?: string;
  signed_off?: boolean;
  created_at: string;
  updated_at?: string;
  result?: {
    final_claim_amount?: number;
  };
}

interface QueueStats {
  available_count: number;
  my_claims_count: number;
  ready_count: number;
  total_portfolio_value: number;
}

const formatCurrency = (value?: number): string => {
  if (!value) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function StaffDashboard() {
  const navigate = useNavigate();
  const { user, getToken } = useAuth();
  const currentAccountantId = user?.sub || user?.id || 'unknown';
  const currentAccountantName = user?.name || user?.email || 'Staff User';

  // State
  const [activeTab, setActiveTab] = useState<'available' | 'my-cases' | 'ready'>('available');
  const [cases, setCases] = useState<QueueCase[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCase, setSelectedCase] = useState<QueueCase | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  // Now-Line stats for Phase A UX
  const [nowLineStats, setNowLineStats] = useState<NowLineStats>({
    needsAttention: { count: 0, breakdown: [] },
    noActionNeeded: { count: 0, breakdown: [] }
  });
  const [nowLineLoading, setNowLineLoading] = useState(true);

  // Fetch Now-Line stats
  const fetchNowLineStats = async () => {
    setNowLineLoading(true);
    try {
      const token = await getToken();
      const data = await api.get<{needs_attention: any; no_action_needed: any}>(
        '/api/v1/queue/nowline-stats?role=data_specialist',
        { headers: { Authorization: `Bearer ${token}` }, showErrorToast: false }
      );
      setNowLineStats({
        needsAttention: {
          count: data.needs_attention?.count || 0,
          breakdown: data.needs_attention?.breakdown || []
        },
        noActionNeeded: {
          count: data.no_action_needed?.count || 0,
          breakdown: data.no_action_needed?.breakdown || []
        }
      });
    } catch (error) {
      console.debug('Now-Line stats not available:', error);
    } finally {
      setNowLineLoading(false);
    }
  };

  // Fetch queue data
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch stats
      const statsData = await api.get('/api/v1/queue/accountant/stats', { headers, showErrorToast: false });
      setStats(statsData);

      // Fetch cases based on active tab
      let casesEndpoint = '/api/v1/queue/accountant/available';
      if (activeTab === 'my-cases') {
        casesEndpoint = '/api/v1/queue/accountant/my-cases';
      } else if (activeTab === 'ready') {
        casesEndpoint = '/api/v1/queue/accountant/available?status=ready';
      }

      const casesData = await api.get(casesEndpoint, { headers, showErrorToast: false });
      setCases(casesData.cases || []);
    } catch (err: any) {
      console.error('Error fetching queue data:', err);
      setError(err.message || 'Failed to load queue data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchNowLineStats();
  }, [activeTab]);

  // Claim a case
  const handleClaim = async (caseId: string) => {
    setActionLoading(caseId);
    try {
      const token = await getToken();
      await api.post(
        `/api/v1/queue/accountant/claim/${caseId}?accountant_id=${encodeURIComponent(currentAccountantId)}&accountant_name=${encodeURIComponent(currentAccountantName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to claim case');
    } finally {
      setActionLoading(null);
    }
  };

  // Release a case
  const handleRelease = async (caseId: string) => {
    setActionLoading(caseId);
    try {
      const token = await getToken();
      await api.post(
        `/api/v1/queue/accountant/release/${caseId}?accountant_id=${encodeURIComponent(currentAccountantId)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Failed to release case');
    } finally {
      setActionLoading(null);
    }
  };

  // Sign off a case
  const handleSignOff = async (caseId: string) => {
    setActionLoading(caseId);
    try {
      const token = await getToken();
      const response = await api.post(
        `/api/v1/case-signoff/${caseId}?accountant_id=${encodeURIComponent(currentAccountantId)}&accountant_name=${encodeURIComponent(currentAccountantName)}`,
        { notes: '', override_warnings: false },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.success) {
        await fetchData();
      } else {
        setError(response.message || 'Sign-off blocked');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sign off case');
    } finally {
      setActionLoading(null);
    }
  };

  // Filter cases by search
  const filteredCases = cases.filter(c => {
    const search = searchQuery.toLowerCase();
    return (
      c.business_name?.toLowerCase().includes(search) ||
      c.client_name?.toLowerCase().includes(search) ||
      c.law_firm?.toLowerCase().includes(search) ||
      c.id?.toLowerCase().includes(search)
    );
  });

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Staff Dashboard</h1>
            <p className="text-gray-500">Welcome back, {currentAccountantName}</p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Now-Line Header - Phase A UX Enhancement */}
        <NowLineHeader
          stats={nowLineStats}
          userName={currentAccountantName}
          loading={nowLineLoading}
          onViewNeedsAttention={() => setActiveTab('available')}
          onViewNoAction={() => setActiveTab('my-cases')}
        />

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setActiveTab('available')}
              className={`bg-white rounded-xl border p-5 text-left transition-all ${
                activeTab === 'available' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200 hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.available_count}</p>
              <p className="text-sm text-gray-500">Available Cases</p>
            </button>

            <button
              onClick={() => setActiveTab('my-cases')}
              className={`bg-white rounded-xl border p-5 text-left transition-all ${
                activeTab === 'my-cases' ? 'border-emerald-500 ring-2 ring-emerald-500/20' : 'border-gray-200 hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.my_claims_count}</p>
              <p className="text-sm text-gray-500">My Cases</p>
            </button>

            <button
              onClick={() => setActiveTab('ready')}
              className={`bg-white rounded-xl border p-5 text-left transition-all ${
                activeTab === 'ready' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-gray-200 hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-green-100">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.ready_count}</p>
              <p className="text-sm text-gray-500">Ready for Attorney</p>
            </button>

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg bg-purple-100">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_portfolio_value)}</p>
              <p className="text-sm text-gray-500">Portfolio Value</p>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cases List (2/3 width) */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search cases..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Tab Header */}
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">
                  {activeTab === 'available' && 'Available Cases'}
                  {activeTab === 'my-cases' && 'My Claimed Cases'}
                  {activeTab === 'ready' && 'Ready for Attorney'}
                  <span className="ml-2 text-sm text-gray-500">({filteredCases.length})</span>
                </h2>
              </div>

              {/* Cases List */}
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                  <p className="text-gray-500">Loading cases...</p>
                </div>
              ) : filteredCases.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">
                    {activeTab === 'available' && 'No available cases'}
                    {activeTab === 'my-cases' && 'You have no claimed cases'}
                    {activeTab === 'ready' && 'No cases ready for attorney'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredCases.map((c) => (
                    <div
                      key={c.id}
                      className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                        selectedCase?.id === c.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => setSelectedCase(c)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900 truncate">
                              {c.business_name || c.client_name || 'Unnamed Case'}
                            </h3>
                            {c.is_ready && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                                Ready
                              </span>
                            )}
                            {c.signed_off && (
                              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                                Signed Off
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                            {c.law_firm && (
                              <span className="flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {c.law_firm}
                              </span>
                            )}
                            {c.result?.final_claim_amount && (
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-3 h-3" />
                                {formatCurrency(c.result.final_claim_amount)}
                              </span>
                            )}
                            {c.claimed_by_name && activeTab !== 'my-cases' && (
                              <span className="text-amber-600">
                                Claimed by {c.claimed_by_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* Action Buttons */}
                          {activeTab === 'available' && !c.claimed_by_id && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleClaim(c.id); }}
                              disabled={actionLoading === c.id}
                              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                              {actionLoading === c.id ? 'Claiming...' : 'Claim'}
                            </button>
                          )}
                          {activeTab === 'my-cases' && (
                            <>
                              {c.is_ready && !c.signed_off && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSignOff(c.id); }}
                                  disabled={actionLoading === c.id}
                                  className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                                >
                                  {actionLoading === c.id ? 'Signing...' : 'Sign Off'}
                                </button>
                              )}
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRelease(c.id); }}
                                disabled={actionLoading === c.id}
                                className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50"
                              >
                                Release
                              </button>
                            </>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/report/${c.id}`); }}
                            className="p-1.5 text-gray-400 hover:text-gray-600"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Side Panel (1/3 width) */}
          <div className="space-y-6">
            {selectedCase ? (
              <>
                {/* Selected Case Info */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {selectedCase.business_name || selectedCase.client_name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">ID: {selectedCase.id}</p>
                  <button
                    onClick={() => navigate(`/report/${selectedCase.id}`)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View Full Report
                  </button>
                </div>

                {/* Readiness Panel */}
                <CaseReadinessPanel
                  caseId={selectedCase.id}
                  compact={false}
                  showRefresh={true}
                />

                {/* Extracted Documents */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <ExtractedDocumentsPanel caseId={selectedCase.id} compact={true} />
                </div>
              </>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Select a case to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
